import React, { useCallback, useRef, useState } from 'react';
import { cn } from '~/lib/utils';
import themes from '~/design-system/components/v1_5/Header/HeaderTheme.module.css';
import { ArtifactFrame } from './ArtifactFrame';
import { BrowserBar } from './BrowserBar';
import { DocumentSurface } from './DocumentSurface';
import { GeneratingPill } from './GeneratingPill';
import { PagerCluster, type PagerClusterProps } from './PagerCluster';
import { PreviewBar, type PreviewBarProps } from './PreviewBar';
import { PreviewStage, StageIndicator } from './PreviewStage';
import { StageMessage, StagePill } from './StageMessage';
import { ZOOM_FIT, ZoomCluster } from './ZoomCluster';
import { ARTIFACT_GUTTER, IMAGE_GUTTER, useArtifactFit } from './useArtifactFit';
import styles from './PreviewPane.module.css';
import type { ArtifactKind, HeaderTheme, PreviewStatus } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';
import { useIsomorphicLayoutEffect } from '~/lib/useIsomorphicLayoutEffect';


/**
 * Copy. Verbatim from the boards, and kept here rather than in props because it is not the host's
 * to phrase: these six strings ARE the states.
 */
/**
 * The empty board, per kind (Beka, 2026-09-05).
 *
 * It used to be three constants that all said "artifact", so the Documents tab with nothing selected
 * offered to write a slide — the pane contradicting the directory two panes to its left. The board is
 * about what you are LOOKING THROUGH, not what the deck is made of, so it takes the pane's own `kind`.
 *
 * Keyed on `ArtifactKind` and not on the directory's kind, because the pane has never heard of the
 * directory: `artifactStage` already translates one into the other, and this is the same translation
 * arriving one prop later. The wording matches the directory's own empty boards deliberately — the two
 * are read together and a different verb for the same act would read as a different act.
 */
const EMPTY_BOARD: Record<ArtifactKind, { icon: string; body: string; cta: string }> = {
  // A screen with three dots under it — the app's own mark for "a slide".
  slide: {
    icon: 'slides',
    body: 'Pick an artifact on the left, or ask the agent to write the next one.',
    cta: 'New artifact',
  },
  document: {
    icon: 'file-text',
    body: 'Pick a document on the left, or ask the agent to write one.',
    cta: 'New document',
  },
  website: {
    icon: 'global',
    body: 'Pick a website on the left, or add one.',
    cta: 'Add website',
  },
  image: {
    icon: 'image',
    body: 'Pick an image on the left, or upload one.',
    cta: 'Upload images',
  },
};

/** One headline for all four: what is absent is the same thing whatever kind you were looking for. */
const EMPTY_TITLE = 'Nothing open';
const FAILED_TITLE = 'This artifact could not be rendered';
const FAILED_BODY = 'The content is safe. This is a render problem, not a data one.';
const FAILED_RETRY = 'Try again';
const FAILED_SOURCE = 'View the source';
const LOADING_LABEL = 'Thinking';
const WRITING_LABEL = 'Writing the artifact';

/** A 16:9 deck slide's own coordinate space. Every number the pane reports is in these units. */
const DEFAULT_NATURAL_WIDTH = 1280;
const DEFAULT_NATURAL_HEIGHT = 720;

export interface PreviewPaneProps {
  theme?: HeaderTheme;

  /* ── the bar ─────────────────────────────────────────────────────────────────────────────── */

  title?: string;
  /** `artifact 02`, `artifact 02 · writing`, `16 sections`, `1920 × 1080`, `checked 5m ago`. */
  meta?: string;
  /** The bar's left end, before the name — the directory fold toggle. See `PreviewBar`. */
  leading?: React.ReactNode;
  /** The bar's trailing cluster. Composed by the host — see `PreviewBar`. */
  trailing?: React.ReactNode;
  /** The bar's far right end, after its `⋯` — the Properties toggle. See `PreviewBar`. */
  end?: React.ReactNode;
  /**
   * Drop the pane's own 36px bar. The merged header (Guri, 2026-08-30) carries the open item's
   * name and controls on the application bar instead; the host that sets this owns re-homing
   * them. The stage keeps everything that was never the bar's — the zoom cluster, the pill, the
   * indicator, and a website's own browser band.
   */
  hideBar?: boolean;
  /**
   * Supply this and the bar grows its own `⋯`, opening the same menu the item's Directory row
   * opens. Passed straight through; see `PreviewBar`.
   */
  onItemAction?: PreviewBarProps['onItemAction'];
  /** The open item's per-kind menu rows and kind, passed straight through to the bar's `RowMenu`. */
  itemMenuRows?: PreviewBarProps['itemMenuRows'];
  itemMenuKind?: PreviewBarProps['itemMenuKind'];
  /** One click on the bar's name opens it for editing. Passed straight through; see `PreviewBar`. */
  onRename?: PreviewBarProps['onRename'];

  /* ── what is on the stage ────────────────────────────────────────────────────────────────── */

  status?: PreviewStatus;
  kind?: ArtifactKind;
  /**
   * The artifact, authored in its OWN coordinate space — a slide at 1280 x 720, an image at its
   * pixel size — and scaled to the pane by the frame. For a document it is the body, unscaled.
   */
  children?: React.ReactNode;
  /**
   * What to put on the stage instead of the `empty` board, when the host has something better to say
   * than "Nothing open".
   *
   * The pane's five states are the pane's, and `empty` is the only one of them that can be WRONG
   * rather than merely plain: it says nothing is open, and a host sometimes has an item open whose
   * emptiness is a question — /deck's website card with no address yet, which asks for one on this
   * stage. So the host supplies the node and the pane keeps the state.
   *
   * A SLOT, not a kind. The pane must not learn what a website is: it is content-agnostic by
   * construction (that is why the artifact arrives as `children`), and a `kind === 'website' &&
   * status === 'empty'` branch in here would put one product's create flow inside the design system,
   * where nothing could reach the write path it needs anyway. Rendered raw — no frame, no mat
   * treatment, no `StageMessage` wrapper — because the host has already decided what it looks like;
   * it lands in the stage's own centred slot, which is where the empty board would have been.
   *
   * Ignored in every other state. It never suppresses `failed`, and it is not a way to draw an
   * artifact.
   */
  emptyOverride?: React.ReactNode;
  /** The artifact's natural size. What `Fit` fits, and what the pane reports about it. */
  naturalWidth?: number;
  naturalHeight?: number;
  /** Lateral breathing room, in screen pixels. Defaults per kind — 37, or 77 for an image. */
  gutter?: number;

  /** The website kind's browser band. Ignored by every other kind. */
  url?: string;
  live?: boolean;
  /** A new address was entered in the browser band. Websites only. */
  onNavigate?: (url: string) => void;
  onReload?: () => void;
  onOpenExternal?: () => void;

  /* ── the floating layer ──────────────────────────────────────────────────────────────────── */

  /** The bottom-centre line, verbatim. `2 of 8`, `PNG · 2.4 MB`, `live · 1440 × 900`. */
  indicator?: React.ReactNode;
  /**
   * The bottom-left pager: back, `3 of 5`, forward, in the zoom cluster's bar (Beka, 2026-09-25). A host
   * that draws it drops the count from `indicator`, which would otherwise say it twice.
   */
  pager?: Omit<PagerClusterProps, 'theme' | 'className'>;
  /** 0–100, while `status` is `writing`. */
  percent?: number;

  /**
   * A floating bar that hangs ABOVE the framed artifact, centred on it and clear of it.
   *
   * The pane owns this placement rather than the host, for the reason the pane owns every other
   * floating thing: the artifact's position is the product of the measured stage, the fit and the
   * zoom, and all three live in here. A host trying to place it would be re-deriving `useArtifactFit`
   * from outside.
   *
   * Anchored to the ARTIFACT and not to the stage, which is what makes it stay put when the pane is
   * resized or the artifact is zoomed — the same argument the generating pill's anchor makes one
   * prop up. Only drawn on a framed artifact, because a document has no card to hang from.
   */
  aboveArtifact?: React.ReactNode;

  /**
   * A control that sits BELOW the framed artifact, centred on the stage's bottom edge: the insert dock
   * (2026-09-27). The mirror of `aboveArtifact`, anchored to the stage for the same reason. It shares the
   * bottom line with the pager and the zoom cluster, each its own piece, all 16px up and 36px tall (Beka,
   * 2026-09-27: in line with navigation and zoom, "not together").
   */
  belowArtifact?: React.ReactNode;

  /** 1 is Fit. Controlled; leave undefined and the pane holds it, so a story zooms standalone. */
  zoom?: number;
  defaultZoom?: number;
  onZoomChange?: (zoom: number) => void;
  /**
   * Force the zoom cluster on or off. By default it appears whenever there is a fitted artifact to
   * zoom — so never on a document, which scrolls, and never on a state with no artifact under it.
   */
  showZoom?: boolean;

  /* ── the two states that offer a way out ─────────────────────────────────────────────────── */

  onNewArtifact?: () => void;
  onRetry?: () => void;
  onViewSource?: () => void;
  /** A press on the mat around the artifact, off the artifact itself (`PreviewStage`): the host deselects. */
  onStageBackgroundPress?: () => void;

  className?: string;
}

/**
 * The Preview pane: its own 36px artifact bar, and a full-bleed stage under it.
 *
 * The caption on the first board is the whole design: "no pane chrome. the artifact is the app, so
 * it sits on the ground." There is one bar, one mat, and everything else floats — the artifact is
 * never in a card, never inset, and never given a panel of its own to sit in.
 *
 * The pane holds two pieces of state and no more: the zoom level, and the measured stage box that
 * turns the artifact's natural size into screen pixels. Everything else is a prop, because
 * everything else belongs to whatever is driving it.
 */
export function PreviewPane({
  theme: themeProp,
  title,
  meta,
  leading,
  trailing,
  end,
  hideBar = false,
  onItemAction,
  itemMenuRows,
  itemMenuKind,
  onRename,
  status = 'ready',
  kind = 'slide',
  children,
  emptyOverride,
  naturalWidth = DEFAULT_NATURAL_WIDTH,
  naturalHeight = DEFAULT_NATURAL_HEIGHT,
  gutter,
  url,
  live = true,
  onNavigate,
  onReload,
  onOpenExternal,
  indicator,
  pager,
  percent,
  aboveArtifact,
  belowArtifact,
  zoom: zoomProp,
  defaultZoom = ZOOM_FIT,
  onZoomChange,
  showZoom,
  onNewArtifact,
  onRetry,
  onViewSource,
  onStageBackgroundPress,
  className,
}: PreviewPaneProps) {
  const theme = useV15Theme(themeProp);
  const stageRef = useRef<HTMLDivElement>(null);

  // Controlled or not, the same way every v1.5 surface does it: a story drags it standalone, a
  // route drives it, and neither has to know which the other chose.
  const [internalZoom, setInternalZoom] = useState(defaultZoom);
  const zoom = zoomProp !== undefined ? zoomProp : internalZoom;
  const setZoom = useCallback(
    (next: number) => {
      if (zoomProp === undefined) setInternalZoom(next);
      onZoomChange?.(next);
    },
    [zoomProp, onZoomChange],
  );

  const generating = status === 'loading' || status === 'writing';
  // An artifact is on the stage in exactly three of the five states. `loading` and `writing` both
  // have one — the whole point of those two boards is that you are watching it arrive.
  const hasArtifact = status === 'ready' || generating;
  /*
   * A frame is a card floating on the mat, and two kinds no longer want one.
   *
   * A document never did — it replaces the mat with its own ground. A WEBSITE now joins it: a site
   * rendered as a 10px-radius card in the middle of a grey field is a picture of a website, and the
   * pane is supposed to be showing the thing itself. Full-bleed also gives the page the whole pane,
   * which is the only width at which a real layout behaves the way it does anywhere else.
   *
   * The zoom cluster follows automatically — it is gated on `framed` — and that is right rather than
   * incidental: zoom scales a fixed-size artifact against its own natural dimensions, and a website
   * that fills the pane has none.
   */
  const framed = hasArtifact && kind !== 'document' && kind !== 'website';
  const bleeds = hasArtifact && (kind === 'document' || kind === 'website');

  const fit = useArtifactFit({
    stageRef,
    naturalWidth,
    naturalHeight,
    gutter: gutter ?? (kind === 'image' ? IMAGE_GUTTER : ARTIFACT_GUTTER),
    zoom,
  });

  // Past Fit the artifact is bigger than the window and the centring gives way to the scroll
  // origin, which is the top-left corner — so without this, stepping to 200% would jump you to the
  // corner of a slide rather than magnifying what you were already looking at. Re-centring the
  // scroll after the fit changes puts the middle back on screen, which is where every board draws
  // it, and the rest is a scroll away. Layout effect, not effect: done after paint it is a visible
  // slide across the pane.
  useIsomorphicLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    stage.scrollLeft = (stage.scrollWidth - stage.clientWidth) / 2;
    stage.scrollTop = (stage.scrollHeight - stage.clientHeight) / 2;
  }, [fit.width, fit.height]);

  /**
   * The pill has two anchors and one identity.
   *
   * On a framed artifact it hangs off the artifact's own top-right corner; a document has no frame
   * to hang from, so there it goes to the stage's. It is rendered here rather than inside the
   * framed branch because that is what used to make `kind="document"` + `status="writing"` render
   * pixel-identical to `ready` — `percent` accepted and dropped, and no `role="status"` region
   * mounted at all, so a screen reader watching a document being written heard nothing. Two pills
   * would be two live regions announcing the same fact, hence one element and two classes.
   */
  const pill = generating ? (
    <GeneratingPill
      theme={theme}
      className={framed ? styles.pill : styles.documentPill}
      label={status === 'writing' ? WRITING_LABEL : LOADING_LABEL}
      percent={status === 'writing' ? percent : undefined}
    />
  ) : null;

  return (
    <div className={cn(themes[theme], styles.pane, className)}>
      {hideBar ? null : (
        <PreviewBar
          theme={theme}
          title={title}
          meta={meta}
          leading={leading}
          trailing={trailing}
          end={end}
          onItemAction={onItemAction}
          itemMenuRows={itemMenuRows}
          itemMenuKind={itemMenuKind}
          onRename={onRename}
        />
      )}

      {/* A second band, and only for a website. It is the pane's chrome rather than the artifact's,
          which is the whole difference between this and the bar it replaces: it follows the app's
          theme, it sits above the content instead of inside it, and it does not move when the page
          below it does.

          `hasArtifact` is what keeps it off an `empty` website — the one the host answers with its
          own intake — and that is not incidental: an address bar over a card that has no address is
          a browser showing a page nobody has named. It comes back with the page, in `ready`. */}
      {kind === 'website' && hasArtifact ? (
        <BrowserBar
          theme={theme}
          url={url ?? ''}
          live={live}
          onNavigate={onNavigate}
          onReload={onReload}
          onOpenExternal={onOpenExternal}
        />
      ) : null}

      <PreviewStage
        ref={stageRef}
        onBackgroundPress={onStageBackgroundPress}
        theme={theme}
        overlays={
          <>
            {/* The bottom line and the cluster share the stage's bottom edge but never the same
                slot: the line is centred and the cluster is in the corner, and the design puts the
                line 2px lower so bare text and a bordered box optically agree. */}
            {indicator ? <StageIndicator theme={theme}>{indicator}</StageIndicator> : null}
            {pager ? <PagerCluster theme={theme} {...pager} /> : null}
            {/* The document's pill only. A framed artifact's rides with the frame, one level down,
                and `generating` is only ever true with an artifact on the stage. */}
            {framed ? null : pill}
            {(showZoom ?? (framed && status === 'ready')) ? (
              <ZoomCluster theme={theme} zoom={zoom} onZoomChange={setZoom} />
            ) : null}
            {/* THE TEXT TOOLBAR IS A FLOATING CONTROL, so it lives out here with the other two.
                It used to hang off the artifact itself, 22px above its top edge, inside the scrolling
                window — which works right up until the artifact is tall enough that its top edge is
                less than a toolbar-and-a-gap below the stage's. Then the bar is in the window's
                NEGATIVE overflow: `.stage` clips it, and `scrollTop` cannot go below zero, so it is
                not merely hidden but unreachable. That is the cut Beka hit on a wide pane, where the
                artifact fits to width and leaves ~60px of headroom against a bar that needs ~62.
                Anchored to the stage it cannot be cut at any zoom, it cannot reach the header above
                it, and it holds still while the artifact scrolls — which is what this slot is for and
                what the zoom cluster and the bottom line already do. See `PreviewStage`. */}
            {framed && aboveArtifact ? (
              <div className={styles.aboveArtifact}>{aboveArtifact}</div>
            ) : null}
            {framed && belowArtifact ? <div className={styles.belowArtifact}>{belowArtifact}</div> : null}
          </>
        }
      >
        {/* The host's own empty state wins outright when it has one — it is not laid over the board
            or beside it, because the board's whole message ("Nothing open", "pick one on the left")
            is the thing being contradicted. See `emptyOverride`. */}
        {status === 'empty' ? (
          emptyOverride ?? (
            <StageMessage
              theme={theme}
              icon={EMPTY_BOARD[kind].icon}
              title={EMPTY_TITLE}
              body={EMPTY_BOARD[kind].body}
              actions={
                <StagePill
                  theme={theme}
                  label={EMPTY_BOARD[kind].cta}
                  icon="add"
                  size="cta"
                  onClick={onNewArtifact}
                />
              }
            />
          )
        ) : null}

        {status === 'failed' ? (
          <StageMessage
            theme={theme}
            icon="error-warning"
            title={FAILED_TITLE}
            body={FAILED_BODY}
            actions={
              <>
                <StagePill theme={theme} label={FAILED_RETRY} variant="solid" onClick={onRetry} />
                <StagePill theme={theme} label={FAILED_SOURCE} onClick={onViewSource} />
              </>
            }
          />
        ) : null}

        {/* Both bleeding kinds land on the same surface: it is the thing that replaces the mat
            with the artifact's own ground, and a website wants that as much as a document does. */}
        {bleeds ? (
          <DocumentSurface theme={theme} variant={kind === 'website' ? 'page' : 'column'}>
            {children}
          </DocumentSurface>
        ) : null}

        {framed ? (
          // The frame's own wrapper, so the generating pill can be positioned against the ARTIFACT
          // rather than against the stage. It is the one floating thing in the pane that moves when
          // the artifact does, because it is a fact about the artifact and not about the pane.
          <div className={styles.artifactSlot} data-measured={fit.measured}>
            <ArtifactFrame
              theme={theme}
              kind={kind}
              naturalWidth={naturalWidth}
              naturalHeight={naturalHeight}
              scale={fit.scale}
              width={fit.width}
              height={fit.height}
              className={generating ? styles.generating : undefined}
            >
              {children}
            </ArtifactFrame>
            {pill}
          </div>
        ) : null}
      </PreviewStage>
    </div>
  );
}
